import { useCallback, useEffect, useState } from 'react';
import { isInstallOfferBlocked, isIos, isStandalone } from '@/pwa/pwaEnvironment';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'autofluxo_pwa_install_dismissed_at';
const DISMISS_DAYS = 30;

let deferredPrompt: BeforeInstallPromptEvent | null = null;

// Captura o evento o quanto antes (o navegador dispara uma única vez).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event('autofluxo:installable'));
  });
}

function wasRecentlyDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || '0');
    if (!at) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function usePwaInstall() {
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed());

  useEffect(() => {
    const onInstallable = () => setCanPrompt(true);
    const onInstalled = () => {
      deferredPrompt = null;
      setCanPrompt(false);
      setInstalled(true);
    };
    window.addEventListener('autofluxo:installable', onInstallable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('autofluxo:installable', onInstallable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable' as const;
    const evt = deferredPrompt;
    // prompt() só pode ser usado uma vez: descarta o evento em qualquer resultado.
    deferredPrompt = null;
    setCanPrompt(false);
    try {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      return outcome;
    } catch {
      return 'unavailable' as const;
    }
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  const blocked = isInstallOfferBlocked();
  const ios = isIos();

  return {
    /** Instalação programática disponível (Android/Chrome/Edge). */
    canPrompt,
    /** Rodando já instalado. */
    installed,
    /** Usuário dispensou recentemente o convite automático. */
    dismissed,
    /** iOS/iPadOS: só instruções manuais. */
    isIos: ios,
    /** Devemos mostrar alguma opção de instalação? */
    showInstallOption: !blocked && !installed && (canPrompt || ios),
    promptInstall,
    dismiss,
  };
}
